/* eslint-disable @typescript-eslint/no-misused-promises */
/* eslint-disable @typescript-eslint/no-floating-promises */
import {
  GALLERY_TYPE,
  IGalleryControllerConfig,
  IProduct,
  ProductsManifest,
  VeloInputDataSource,
  VeloInputs,
} from '../types/galleryTypes';
import {
  ALL_PRODUCTS_PRODUCTS_LOGIC,
  COLLECTION_PRODUCTS_LOGIC,
  DEFAULT_COLLECTION_ID,
  Experiments,
  FORCE_RELATED_GALLERY_RENDER_TIMEOUT,
  GALLERY_PUBLIC_DATA_PRESET_ID,
  RELATED_PRODUCTS_PRODUCTS_LOGIC,
  SLIDER_GALLERY_TYPE,
  translationPath,
} from '../constants';
import {IGalleryStyleParams, IPropsInjectedByViewerScript, ITextsMap} from '../types/sliderGalleryTypes';
import {ProductsService} from '../services/ProductsService';
import {SiteStore} from '@wix/wixstores-client-storefront-sdk/dist/es/src/viewer-script/site-store/SiteStore';
import {getTranslations} from '@wix/wixstores-client-storefront-sdk/dist/es/src/viewer-script/utils';
import {getStyleParamsWithDefaults} from '@wix/wixstores-client-common-components/dist/src/outOfIframes/defaultStyleParams/getStyleParamsWithDefaults';
import {
  ActionStatus,
  AddToCartActionOption,
  APP_DEFINITION_ID,
  PageMap,
  PubSubEvents,
} from '@wix/wixstores-client-core/dist/es/src/constants';
import {getStyleParamsWithDefaultsFunc} from '../getStyleParamsWithDefaultsFunc';
import {MultilingualService} from '@wix/wixstores-client-storefront-sdk/dist/es/src/services/MultilingualService/MultilingualService';
import {IWidgetControllerConfig} from '@wix/native-components-infra/dist/es/src/types/types';

import {AddToCartService} from '@wix/wixstores-client-storefront-sdk/dist/es/src/services/AddToCartService/AddToCartService';
import {actualPrice, isPreOrder} from '@wix/wixstores-client-core/dist/es/src/productOptions/productUtils';
import {CustomUrlApi} from '@wix/wixstores-client-storefront-sdk/dist/es/src/utils/CustomUrl/CustomUrlApi';
import {BaseGalleryStore} from './BaseGalleryStore';
import {ProductPriceRange} from '@wix/wixstores-client-storefront-sdk/dist/es/src/services/ProductPriceRange/ProductPriceRange';
import {unitsTranslations} from '../common/components/ProductItem/ProductPrice/unitsTranslations';
import {ProductPriceBreakdown} from '@wix/wixstores-client-storefront-sdk/dist/es/src/services/ProductPriceBreakdown/ProductPriceBreakdown';
import _ from 'lodash';
import {ProductFilters} from '../graphql/queries-schema';

export class SliderGalleryStore extends BaseGalleryStore {
  private readonly fedopsLogger;
  private mainCollectionId: string;
  private multilingualService: MultilingualService;
  private productPageSectionUrl: string;
  private translations: any = {};
  private isFedopsReport: boolean = true;
  private relatedProductIds: string[] = [];
  private addedToCartStatus: {[p: string]: ActionStatus} = {};
  private readonly customUrlApi: CustomUrlApi;
  private isUrlWithOverrides: boolean = false;

  constructor(
    styleParams: IGalleryStyleParams,
    config: IWidgetControllerConfig['config'],
    updateComponent: (props: Partial<IPropsInjectedByViewerScript>) => void,
    siteStore: SiteStore,
    private readonly compId: string,
    private readonly reportError: (e) => any,
    private readonly type: string,
    private veloInputs?: VeloInputs
  ) {
    super({config}, siteStore, styleParams, updateComponent);
    const fedopsLoggerFactory = this.siteStore.platformServices.fedOpsLoggerFactory;
    this.fedopsLogger = fedopsLoggerFactory.getLoggerForWidget({
      appId: APP_DEFINITION_ID,
      widgetId: this.type,
    });
    const numOfColumns = styleParams.numbers.galleryColumns || 4;
    const initialProductsCount = numOfColumns * 4;
    this.addToCartService = new AddToCartService(this.siteStore, this.config.publicData);
    this.productsService = new ProductsService(
      this.siteStore,
      initialProductsCount,
      'Slider Gallery',
      this.shouldShowProductOptions,
      this.shouldRenderPriceRange,
      this.fedopsLogger,
      false
    );
    this.customUrlApi = new CustomUrlApi(this.siteStore.location.buildCustomizedUrl);
    this.subscribe();
    this.handleUrlChange();
  }

  private get shouldRenderPriceRange(): boolean {
    return new ProductPriceRange(this.siteStore).shouldShowPriceRange();
  }

  public setVeloInputs(veloInputs: VeloInputs) {
    this.veloInputs = veloInputs;
  }

  private handleUrlChange() {
    this.siteStore.location.onChange(async () => {
      await this.setInitialState();
    });
  }

  public async getCateogryProducts({limit, offset}: {limit: number; offset?: number}): Promise<void> {
    await this.productsService.getCategoryProducts(
      {
        compId: this.compId,
        limit,
        offset,
      },
      this.siteStore.experiments.enabled(Experiments.PreOrderClient)
    );
    this.productsManifest = this.generateProductsManifest(this.productsService.products);
    this.updateComponent({
      products: this.productsService.products,
      productsManifest: this.productsManifest,
      productsVariantInfoMap: this.productsVariantInfoMap,
    });
  }

  private padProductListWithPlaceholders() {
    if (this.productsService.products.length < this.productsService.totalCount) {
      const realLength = this.productsService.products.length;
      this.productsService.products[this.productsService.totalCount - 1] = undefined;
      this.productsService.products.fill({isFake: true} as any, realLength, this.productsService.totalCount);
    }
  }

  public async setInitialState(forceRender: boolean = false): Promise<void> {
    if (!forceRender && !this.shouldComponentRender()) {
      return this.updateComponent({
        isLoaded: false,
      });
    }

    const numOfColumns = this.config.style.styleParams.numbers.galleryColumns || 4;
    const numOfSets = 2;

    const fetcher = this.getDataFetcher();
    const [translations, response, {url}] = await Promise.all([
      getTranslations(translationPath(this.siteStore.baseUrls.galleryBaseUrl, this.siteStore.locale)),
      fetcher(),
      this.siteStore.getSectionUrl(PageMap.PRODUCT),
    ]).catch(this.reportError);

    this.isUrlWithOverrides = await this.customUrlApi.init();

    if (
      this.galleryType !== GALLERY_TYPE.RELATED_PRODUCTS &&
      this.galleryType !== GALLERY_TYPE.VELO_PRODUCTS &&
      this.productsService.totalCount > numOfSets * numOfColumns
    ) {
      this.padProductListWithPlaceholders();
      await this.productsService.getCategoryProducts(
        {
          compId: this.compId,
          limit: numOfColumns * numOfSets,
          offset: this.productsService.totalCount - numOfColumns * numOfSets,
        },
        this.siteStore.experiments.enabled(Experiments.PreOrderClient)
      );
    }

    const {products} = this.productsService;
    this.translations = translations;

    this.productPriceBreakdown = new ProductPriceBreakdown(this.siteStore, this.translations, {
      excludedPattern: 'gallery.price.tax.excludedParam.label',
      includedKey: 'gallery.price.tax.included.label',
      includedPattern: 'gallery.price.tax.includedParam.label',
      excludedKey: 'gallery.price.tax.excluded.label',
    });

    this.productPageSectionUrl = url;
    this.mainCollectionId = this.productsService.getMainCollectionId();
    this.productsManifest = this.generateProductsManifest(products);
    this.multilingualService = new MultilingualService(
      this.siteStore,
      this.publicData.COMPONENT,
      response.appSettings.widgetSettings
    );
    this.updateComponent(this.getPropsToInject(products));

    if (!this.siteStore.isSSR()) {
      this.productsService.storeNavigation(this.siteStore.siteApis.currentPage.id);
    }
  }

  public onAppLoaded(): void {
    /* istanbul ignore next: hard to test it */
    if (this.isFedopsReport) {
      this.isFedopsReport = false;

      /* istanbul ignore next: debug */
      if (
        this.siteStore.experiments.enabled('specs.stores.ReportBiForEmptyGallery') &&
        !this.productsService.products.length
      ) {
        this.siteStore.biLogger.exposureEventForTests({testName: `empty-gallery-slider`});
      }

      this.siteStore.biLogger.viewGallerySf({
        isMobileFriendly: this.siteStore.isMobileFriendly,
        addToCart: this.shouldShowAddToCartButton,
        hasPrice: this.styleParams.booleans.gallery_showPrice,
        hasOptions: this.shouldShowProductOptions,
        hasQuantity: this.shouldShowQuantity,
        hasQuickView: this.styleParams.booleans.showQuickView,
        hoverType: this.styleParams.fonts.gallery_hoverType.value,
        priceBreakdown: this.productPriceBreakdown.priceBreakdownBIParam,
        navigationClick: this.getNavigationClick(),
        productsLogic:
          // eslint-disable-next-line no-nested-ternary
          this.galleryType === GALLERY_TYPE.RELATED_PRODUCTS
            ? RELATED_PRODUCTS_PRODUCTS_LOGIC
            : this.mainCollectionId !== DEFAULT_COLLECTION_ID
            ? COLLECTION_PRODUCTS_LOGIC
            : ALL_PRODUCTS_PRODUCTS_LOGIC,
        type: SLIDER_GALLERY_TYPE,
      });
    }
  }

  private getNavigationClick(): string {
    if (!this.styleParams.booleans.gallery_showAddToCartButton) {
      return '';
    } else if (this.getAddToCartAction() === AddToCartActionOption.MINI_CART) {
      return 'mini-cart';
    } else if (this.getAddToCartAction() === AddToCartActionOption.CART) {
      return 'cart';
    }
    return 'none';
  }

  private getPropsToInject(products: IProduct[]): IPropsInjectedByViewerScript {
    return {
      ...this.getCommonPropsToInject(),
      allowFreeProducts: this.addToCartService.allowFreeProducts,
      addedToCartStatus: this.addedToCartStatus,
      cssBaseUrl: this.siteStore.baseUrls.galleryBaseUrl,
      getCategoryProducts: this.getCateogryProducts.bind(this),
      handleProductItemClick: this.handleProductItemClick.bind(this),
      handleProductsOptionsChange: this.handleOptionSelectionsChange.bind(this),
      isFirstPage: true,
      isInteractive: this.siteStore.isInteractive(),
      isLiveSiteMode: this.siteStore.isSiteMode(),
      isLoaded: true,
      showShowLightEmptyState: this.productsService.hideGallery,
      hideGallery: this.productsService.hideGallery,
      shouldShowMobile: this.siteStore.isMobile(),
      isRTL: this.siteStore.isRTL(),
      mainCollectionId: this.mainCollectionId,
      onAppLoaded: this.onAppLoaded.bind(this),
      openQuickView: this.openQuickView.bind(this),
      productsManifest: this.productsManifest,
      productsVariantInfoMap: this.productsVariantInfoMap,
      products,
      shouldShowAddToCartButton: this.shouldShowAddToCartButton,
      shouldShowQuantity: this.shouldShowQuantity,
      shouldShowProductOptions: this.shouldShowProductOptions,
      showTitle: this.shouldShowTitle,
      ravenUserContextOverrides: {id: this.siteStore.storeId, uuid: this.siteStore.uuid},
      textsMap: this.getTextsMap(),
      totalProducts: this.productsService.totalCount,
      shouldShowAddToCartSuccessAnimation: this.getAddToCartAction() === AddToCartActionOption.NONE,
      handleAddToCart: this.handleAddToCart.bind(this),
      updateAddToCartStatus: this.updateAddToCartStatus.bind(this),
      productsPriceRangeMap: this.productsService.productPriceRangeMap,
      experiments: {
        isUpliftGalleryRibbonFrontEnabled: this.siteStore.experiments.enabled(Experiments.UpliftGalleryRibbonFront),
        isHideRibbonAtGalleryFrontEnabled: this.siteStore.experiments.enabled(Experiments.HideRibbonAtGalleryFront),
        isPreOrderEnabled: this.siteStore.experiments.enabled(Experiments.PreOrderClient),
        isArrowlessMobileSliderEnabled: this.siteStore.experiments.enabled(
          Experiments.ClientGalleryArrowlessMobileSlider
        ),
        basePriceWithRTLSupport: this.siteStore.experiments.enabled(Experiments.BasePriceWithRTLSupport),
        newUiTpaImage: this.siteStore.experiments.enabled(Experiments.NewUiTpaImage),
        isAllowGalleryProductRoundCornersInViewer: this.siteStore.experiments.enabled(
          Experiments.AllowGalleryProductRoundCornersInViewer
        ),
        fixProductOptionsLayoutBugs: this.siteStore.experiments.enabled(Experiments.FixProductOptionsLayoutBugs),
      },
    };
  }

  private getTextsMap(): ITextsMap {
    return {
      addToCartContactSeller: this.translations['gallery.contactSeller.button'],
      addToCartOutOfStock:
        this.multilingualService.get('gallery_oosButtonText') || this.translations['gallery.outOfStock.button'],
      addToCartSuccessSR: this.translations['gallery.sr.addToCartSuccess'],
      digitalProductBadgeAriaLabelText: this.translations['sr.digitalProduct'],
      galleryAddToCartButtonText:
        this.multilingualService.get('gallery_addToCartText') || this.translations['gallery.addToCart.button'],
      galleryAddToCartPreOrderButtonText:
        this.multilingualService.get('gallery_preOrderText') || this.translations['gallery.preOrder.button'],
      noProductsMessageText: this.translations.NO_PRODUCTS_MESSAGE_MAIN,
      productOutOfStockText:
        this.multilingualService.get('gallery_oosButtonText') || this.translations.OUT_OF_STOCK_LABEL,
      productPriceAfterDiscountSR: this.translations['sr.PRODUCT_PRICE_AFTER_DISCOUNT'],
      productPriceBeforeDiscountSR: this.translations['sr.PRODUCT_PRICE_BEFORE_DISCOUNT'],
      productPriceWhenThereIsNoDiscountSR: this.translations['sr.PRODUCT_PRICE_WHEN_THERE_IS_NO_DISCOUNT'],
      quantityAddSR: this.translations['gallery.sr.addQty'],
      quantityChooseAmountSR: this.translations['gallery.sr.chooseQty'],
      quantityInputSR: this.translations['gallery.sr.quantity'],
      quantityMaximumAmountSR: this.translations['gallery.exceedsQuantity.error'],
      quantityMinimumAmountSR: this.translations['gallery.minimumQuantity.error'],
      quantityRemoveSR: this.translations['gallery.sr.removeQty'],
      quantityTotalSR: this.translations['gallery.sr.totalQty'],
      quickViewButtonText: this.translations.QUICK_VIEW,
      sliderGalleryNextProduct: this.translations['sr.sliderGallery.nextProduct'],
      sliderGalleryNoProductsMessageText: this.translations.NO_PRODUCTS_MESSAGE_MINI_GALLERY_MAIN,
      sliderGalleryPreviousProduct: this.translations['sr.sliderGallery.previousProduct'],
      sliderGalleryTitle: this.title,
      priceRangeText: this.translations['gallery.price.from.label'],
      pricePerUnitSR: this.translations['gallery.sr.units.basePrice.label'],
      arrowPrevious: this.translations['gallery.sr.carousel.previous.label'],
      carouselContainerLabel: this.translations['gallery.sr.carousel.container.label'],
      arrowNext: this.translations['gallery.sr.carousel.next.label'],
      measurementUnits: this.getMeasurementUnitsTranslation(),
    };
  }

  private getMeasurementUnitsTranslation() {
    return _.reduce(
      unitsTranslations,
      (result, types, unit) => {
        result[unit] = result[unit] || {};
        _.each(types, (translationKey, type) => {
          result[unit][type] = this.translations[translationKey];
        });
        return result;
      },
      {}
    );
  }

  private generateProductsManifest(products: IProduct[]): ProductsManifest {
    return products
      .filter(({urlPart}) => Boolean(urlPart))
      .reduce((acc, product) => {
        acc[product.id] = {
          url: this.getProductPageUrl(product.urlPart),
          addToCartState: this.addToCartService.getButtonState({
            price: actualPrice(product),
            inStock: product.isInStock,
            isPreOrderState: this.siteStore.experiments.enabled(Experiments.PreOrderClient) && isPreOrder(product),
          }),
        };
        return acc;
      }, {});
  }

  private getProductPageUrl(slug) {
    return /* istanbul ignore next: test */ this.isUrlWithOverrides
      ? this.customUrlApi.buildProductPageUrl({slug})
      : `${this.productPageSectionUrl}/${slug}`;
  }

  private async handleAddToCart({productId, index, quantity}: {productId: string; index: number; quantity: number}) {
    await this.productsService.addToCart({
      productId,
      quantity,
      index,
      compId: this.compId,
      externalId: this.config.externalId,
      addToCartAction: this.getAddToCartAction(),
      isPreOrderEnabled: this.siteStore.experiments.enabled(Experiments.PreOrderClient),
    });

    this.updateAddToCartStatus(productId, ActionStatus.SUCCESSFUL);
  }

  private readonly getAddToCartAction = () => this.styleParams.numbers.gallery_addToCartAction;

  private readonly updateAddToCartStatus = (productId: string, status: ActionStatus) => {
    this.addedToCartStatus = {
      ...this.addedToCartStatus,
      [productId]: status,
    };

    this.updateComponent({addedToCartStatus: this.addedToCartStatus});
  };

  private openQuickView({productId, index}: {productId: string; index: number}) {
    const galleryProductsLogic =
      // eslint-disable-next-line no-nested-ternary
      this.galleryType === GALLERY_TYPE.RELATED_PRODUCTS
        ? RELATED_PRODUCTS_PRODUCTS_LOGIC
        : this.mainCollectionId !== DEFAULT_COLLECTION_ID
        ? COLLECTION_PRODUCTS_LOGIC
        : ALL_PRODUCTS_PRODUCTS_LOGIC;
    return this.productsService.quickViewProduct(productId, index, {
      compId: this.compId,
      externalId: this.config.externalId,
      galleryType: 'slider-gallery',
      galleryProductsLogic,
    });
  }

  private handleProductItemClick({
    biData: {productId, index},
  }: {
    biData: {
      productId: string;
      index: number;
    };
  }) {
    const product = this.productsService.getProduct(productId);

    this.productsService.storeNavigation(this.siteStore.siteApis.currentPage.id);

    const galleryProductsLogic =
      // eslint-disable-next-line no-nested-ternary
      this.galleryType === GALLERY_TYPE.RELATED_PRODUCTS
        ? 'related products'
        : this.mainCollectionId !== DEFAULT_COLLECTION_ID
        ? 'collection'
        : 'All products';
    this.siteStore.biLogger.clickOnProductBoxSf({
      productId,
      hasRibbon: !!product.ribbon,
      hasOptions: !!product.options.length,
      index,
      productType: product.productType,
      galleryType: 'slider-gallery',
      galleryProductsLogic,
    });

    this.siteStore.navigate(
      {
        sectionId: PageMap.PRODUCT,
        state: product.urlPart,
        queryParams: undefined,
      },
      true
    );
    this.productsService.sendClickTrackEvent(product, index);
  }

  public async updateState(
    newStyleParams: IGalleryStyleParams,
    newPublicData: IGalleryControllerConfig['publicData'] & {appSettings?: any}
  ): Promise<void> {
    this.config.publicData = newPublicData;
    this.multilingualService.setPublicData(newPublicData.COMPONENT);
    const nextStyleParams = getStyleParamsWithDefaults(newStyleParams, () =>
      getStyleParamsWithDefaultsFunc({style: {styleParams: newStyleParams}, dimensions: undefined})
    );

    if (newPublicData.appSettings) {
      this.multilingualService.setWidgetSettings(newPublicData.appSettings);
    }

    const shouldUpdateWithOptions = this.shouldUpdateProductOptions(nextStyleParams);
    this.styleParams = nextStyleParams;

    if (shouldUpdateWithOptions) {
      this.updateWithOptions();
      await this.setInitialState(true);
    }

    this.updateComponent({
      ...this.getCommonPropsToUpdate(),
      showTitle: this.shouldShowTitle,
      textsMap: this.getTextsMap(),
      productsPriceRangeMap: this.productsService.productPriceRangeMap,
      shouldShowAddToCartButton: this.shouldShowAddToCartButton,
      shouldShowQuantity: this.shouldShowQuantity,
      shouldShowProductOptions: this.shouldShowProductOptions,
    });
  }

  private get isShouldUseVeloInputs() {
    const isExperimentOpen =
      this.siteStore.experiments.enabled(Experiments.GallerySetProductsVelo) ||
      this.siteStore.experiments.enabled(Experiments.SetGalleryCollectionVelo);

    return isExperimentOpen && this.veloInputs;
  }

  private get galleryType() {
    if (this.isShouldUseVeloInputs) {
      return GALLERY_TYPE.VELO_PRODUCTS;
    }

    if (this.publicData.COMPONENT.presetId === GALLERY_PUBLIC_DATA_PRESET_ID.RELATED_PRODUCTS) {
      return GALLERY_TYPE.RELATED_PRODUCTS;
    }

    return GALLERY_TYPE.COLLECTION;
  }

  private getDataFetcher() {
    const dataFetcher = {
      [GALLERY_TYPE.COLLECTION]: () =>
        this.productsService.oldGetInitialData(
          {
            externalId: this.config.externalId,
            compId: this.compId,
          },
          this.siteStore.experiments.enabled(Experiments.PreOrderClient)
        ),
      [GALLERY_TYPE.RELATED_PRODUCTS]: () =>
        this.productsService.getRelatedItems(
          {
            externalId: this.config.externalId,
            productIds: this.relatedProductIds,
          },
          this.siteStore.experiments.enabled(Experiments.PreOrderClient)
        ),
    };

    if (this.isShouldUseVeloInputs) {
      dataFetcher[GALLERY_TYPE.VELO_PRODUCTS] = () => {
        return this.productsService.getInitialData(
          {
            externalId: this.config.externalId,
            compId: this.compId,
            sort: null,
            filters:
              this.siteStore.experiments.enabled(Experiments.GallerySetProductsVelo) &&
              this.veloInputs.sourceType === VeloInputDataSource.Products
                ? ({
                    term: {
                      field: 'id',
                      op: 'IN',
                      values: this.veloInputs.productIds,
                    },
                  } as ProductFilters)
                : null,
            limit: null,
            offset: 0,
            mainCollectionId:
              this.siteStore.experiments.enabled(Experiments.SetGalleryCollectionVelo) &&
              this.veloInputs.sourceType === VeloInputDataSource.Collection
                ? this.veloInputs.collectionId
                : this.mainCollectionId,
          },
          this.siteStore.experiments.enabled(Experiments.IsSplitToBatchesEnabled)
        );
      };
    }

    return dataFetcher[this.galleryType];
  }

  private subscribe() {
    const forceRenderWhenEmpty = () => this.relatedProductIds.length === 0 && this.renderRelatedProducts();

    if (this.galleryType === GALLERY_TYPE.RELATED_PRODUCTS) {
      setTimeout(forceRenderWhenEmpty, FORCE_RELATED_GALLERY_RENDER_TIMEOUT);
      this.siteStore.pubSub.subscribe(
        PubSubEvents.RELATED_PRODUCTS,
        (response) => this.renderRelatedProducts(response.data),
        true
      );
    }
  }

  private renderRelatedProducts(productIds: string[] = []) {
    this.relatedProductIds = [...productIds];
    this.setInitialState(true);
  }

  private shouldComponentRender() {
    if (!this.siteStore.isInteractive()) {
      return true;
    }
    if (this.galleryType === GALLERY_TYPE.RELATED_PRODUCTS && this.relatedProductIds.length === 0) {
      return false;
    }
    return true;
  }

  private get title(): string {
    if (this.galleryType === GALLERY_TYPE.COLLECTION) {
      const collectionName =
        this.productsService.getMainCollectionId() === DEFAULT_COLLECTION_ID
          ? this.translations['gallery.collection.allProducts']
          : this.productsService.collectionName;

      return this.multilingualService.get('SLIDER_GALLERY_TITLE_COLLECTION') || collectionName;
    }

    return (
      this.multilingualService.get('SLIDER_GALLERY_TITLE_RELATED_PRODUCTS') ||
      this.translations['gallery.relatedProducts.default.title']
    );
  }

  private get shouldShowAddToCartButton() {
    return this.styleParams.booleans.gallery_showAddToCartButton;
  }

  private get shouldShowQuantity() {
    return this.styleParams.booleans.gallery_showAddToCartQuantity;
  }

  protected get shouldShowProductOptions(): boolean {
    if (!this.siteStore.experiments.enabled(Experiments.AllowProductOptionsInSliderGallery)) {
      return false;
    }
    return this.styleParams.booleans.gallery_showProductOptionsButton;
  }

  private shouldUpdateProductOptions(nextStyleParams): boolean {
    if (!this.siteStore.experiments.enabled(Experiments.AllowProductOptionsInSliderGallery)) {
      return false;
    }
    return (
      this.styleParams.booleans.gallery_showProductOptionsButton !==
      nextStyleParams.booleans.gallery_showProductOptionsButton
    );
  }

  private updateWithOptions() {
    this.productsService.setWithOptions(this.shouldShowProductOptions);
  }

  private get shouldShowTitle() {
    if (this.styleParams.booleans.gallery_showTitle !== undefined) {
      return this.styleParams.booleans.gallery_showTitle;
    }
    return this.galleryType === GALLERY_TYPE.RELATED_PRODUCTS;
  }
}
